import { getNativePlatform } from './nativeRuntime'
import {
  PREVIEW_DEVICE_OVERRIDE_CHANGE_EVENT,
  readPreviewDeviceOverride,
  type PreviewDeviceOverride,
} from './previewDeviceOverride'

export interface VisualViewportSnapshot {
  height: number
  offsetLeft: number
  offsetTop: number
  scale?: number
  width: number
}

interface VisualViewportMeasurement {
  editableFocused?: boolean
  layoutHeight: number
  layoutWidth: number
  viewport?: VisualViewportSnapshot | null
}

interface LayoutResizeMeasurement {
  editableFocused?: boolean
  layoutHeight: number
  layoutWidth: number
  stableLayoutHeight: number
  viewport?: VisualViewportSnapshot | null
}

interface ViewportRuntimeMeasurement {
  nativePlatform?: 'android' | 'ios' | null
  previewDeviceOverride?: PreviewDeviceOverride
  primaryPointer?: 'coarse' | 'fine'
  standalone: boolean
  userAgent: string
}

export interface ViewportRuntime {
  displayMode: 'browser' | 'capacitor' | 'standalone'
  formFactor: 'desktop' | 'phone' | 'tablet'
  geometryMode: 'layout-resize' | 'visual-viewport'
  inputMode: 'pointer' | 'touch'
  platform: 'android' | 'ios' | 'other'
}

export interface VisualViewportMetrics extends VisualViewportSnapshot {
  bottomInset: number
  keyboardVisible: boolean
}

const MINIMUM_KEYBOARD_OCCLUSION_PX = 120
const KEYBOARD_OCCLUSION_RATIO = 0.18

const VIEWPORT_CSS_PROPERTIES = [
  '--visual-viewport-bottom-inset',
  '--visual-viewport-height',
  '--visual-viewport-offset-left',
  '--visual-viewport-offset-top',
  '--visual-viewport-width',
] as const

export function classifyViewportRuntime({
  nativePlatform = null,
  previewDeviceOverride = 'off',
  primaryPointer,
  standalone,
  userAgent,
}: ViewportRuntimeMeasurement): ViewportRuntime {
  const android = nativePlatform === 'android' || /Android/i.test(userAgent)
  const ios = nativePlatform === 'ios' || /iPad|iPhone|iPod/i.test(userAgent)
  const androidChromium = android && /Chrome|Chromium|EdgA/i.test(userAgent)
  const formFactor = android
    ? /Mobile/i.test(userAgent) ? 'phone' : 'tablet'
    : /iPad/i.test(userAgent) ? 'tablet'
      : /iPhone|iPod/i.test(userAgent) ? 'phone' : 'desktop'

  const runtime: ViewportRuntime = {
    displayMode: nativePlatform ? 'capacitor' : standalone ? 'standalone' : 'browser',
    formFactor,
    geometryMode: androidChromium ? 'layout-resize' : 'visual-viewport',
    inputMode: previewDeviceOverride !== 'off'
      ? 'touch'
      : primaryPointer === 'coarse'
        ? 'touch'
        : primaryPointer === 'fine'
          ? 'pointer'
          : android || ios
            ? 'touch'
            : 'pointer',
    platform: android ? 'android' : ios ? 'ios' : 'other',
  }
  // Preview overrides intentionally leave geometry ownership with the host browser.
  // A desktop browser cannot faithfully emulate Android's layout-resize keyboard path.
  if (previewDeviceOverride !== 'off') {
    return {
      ...runtime,
      formFactor: previewDeviceOverride === 'android-phone' ? 'phone' : 'tablet',
      platform: 'android',
    }
  }
  return runtime
}

export function calculateLayoutResizeMetrics({
  editableFocused = false,
  layoutHeight,
  layoutWidth,
  stableLayoutHeight,
  viewport,
}: LayoutResizeMeasurement): VisualViewportMetrics {
  const layoutHeightLoss = Math.max(0, stableLayoutHeight - layoutHeight)
  const minimumKeyboardOcclusion = Math.max(
    MINIMUM_KEYBOARD_OCCLUSION_PX,
    stableLayoutHeight * KEYBOARD_OCCLUSION_RATIO,
  )
  const visualHeight = Math.min(layoutHeight, viewport?.height ?? layoutHeight)
  const visualHeightLoss = Math.max(0, stableLayoutHeight - visualHeight)
  const visualHeightIsKeyboardFallback =
    editableFocused
    && layoutHeightLoss < minimumKeyboardOcclusion
    && Math.abs((viewport?.scale ?? 1) - 1) < 0.01
    && visualHeightLoss >= minimumKeyboardOcclusion
  const layoutHeightShowsKeyboard =
    editableFocused && layoutHeightLoss >= minimumKeyboardOcclusion

  return {
    bottomInset: 0,
    height: visualHeightIsKeyboardFallback ? visualHeight : layoutHeight,
    keyboardVisible: layoutHeightShowsKeyboard || visualHeightIsKeyboardFallback,
    offsetLeft: 0,
    offsetTop: 0,
    width: layoutWidth,
  }
}

export function calculateVisualViewportMetrics({
  editableFocused = false,
  layoutHeight,
  layoutWidth,
  viewport,
}: VisualViewportMeasurement): VisualViewportMetrics {
  const height = viewport?.height ?? layoutHeight
  const width = viewport?.width ?? layoutWidth
  const offsetLeft = viewport?.offsetLeft ?? 0
  const offsetTop = viewport?.offsetTop ?? 0
  const heightLoss = Math.max(0, layoutHeight - height)
  const minimumKeyboardOcclusion = Math.max(
    MINIMUM_KEYBOARD_OCCLUSION_PX,
    layoutHeight * KEYBOARD_OCCLUSION_RATIO,
  )
  const scale = viewport?.scale ?? 1
  const keyboardVisible =
    editableFocused &&
    Math.abs(scale - 1) < 0.01 &&
    heightLoss >= minimumKeyboardOcclusion
  const obscuredBottom = Math.max(0, layoutHeight - height - offsetTop)

  return {
    bottomInset: keyboardVisible ? obscuredBottom : 0,
    height,
    keyboardVisible,
    offsetLeft,
    offsetTop,
    width,
  }
}

function isEditableTarget(target: Element | null): boolean {
  if (!target) return false
  const element = target as HTMLElement
  if (element.isContentEditable) return true
  if (element.tagName === 'TEXTAREA') return !element.hasAttribute('readonly')
  if (element.tagName !== 'INPUT' || element.hasAttribute('readonly')) return false
  const type = (element.getAttribute('type') ?? 'text').toLowerCase()
  return !['button', 'checkbox', 'file', 'hidden', 'image', 'radio', 'reset', 'submit'].includes(type)
}

// Room kept between the line being edited and whatever covers the bottom (the keyboard, the formatting bar).
const REVEAL_MARGIN = 16

/**
 * Where the focused editable's insertion point is: the caret's line in rich text, the field itself otherwise, or the whole panel a
 * field belongs to when that panel is marked `data-reveal-whole` (the tag editor: its field, suggestions and Done).
 */
function focusedLineRect(ownerDocument: Document): DOMRect | null {
  const element = ownerDocument.activeElement as HTMLElement | null
  if (!element || !isEditableTarget(element)) return null
  if (!element.isContentEditable) return (element.closest('[data-reveal-whole]') ?? element).getBoundingClientRect()
  const selection = ownerDocument.getSelection()
  if (!selection?.rangeCount || !element.contains(selection.focusNode)) return null
  const range = selection.getRangeAt(0).cloneRange()
  range.collapse(false)
  const rect = range.getClientRects()[0] ?? range.getBoundingClientRect()
  if (rect && (rect.height || rect.top)) return rect
  // An empty line has no caret box: its block stands in for it.
  const block = (selection.focusNode instanceof Element ? selection.focusNode : selection.focusNode?.parentElement)
  return block?.getBoundingClientRect() ?? null
}

function scrollParent(element: Element | null): HTMLElement | null {
  for (let node = element?.parentElement ?? null; node; node = node.parentElement) {
    const overflow = getComputedStyle(node).overflowY
    if ((overflow === 'auto' || overflow === 'scroll') && node.scrollHeight > node.clientHeight) return node
  }
  return null
}

/**
 * Keeps the line being edited in view (docs/DEVICE_VIEWPORT_STANDARDS.md): when the keyboard opens or resizes, focus moves, or the
 * formatting bar appears and shortens the note. The WebView only keeps the focused element in view, and a long rich-text editor counts
 * as in view while its caret line is under the keyboard; an input at a note's foot (its tags) can be left below it too. The caret's
 * line, or the focused field, is scrolled to sit above the bottom edge with a little room. Nothing moves when it is already visible.
 */
export function revealFocusedLine(browserWindow: Window = window, ownerDocument: Document = document): void {
  const line = focusedLineRect(ownerDocument)
  const scroller = scrollParent(ownerDocument.activeElement)
  if (!line || !scroller) return
  const box = scroller.getBoundingClientRect()
  const visibleBottom = Math.min(box.bottom, browserWindow.visualViewport?.height ?? browserWindow.innerHeight) - REVEAL_MARGIN
  const visibleTop = box.top + REVEAL_MARGIN
  if (line.bottom > visibleBottom) scroller.scrollTop += Math.min(line.bottom - visibleBottom, Math.max(0, line.top - visibleTop))
  else if (line.top < visibleTop) scroller.scrollTop -= visibleTop - line.top
}

function getInitialStableLayoutHeight(
  browserWindow: Window,
  editableFocused: boolean,
): number {
  const layoutHeight = browserWindow.innerHeight
  const layoutWidth = browserWindow.innerWidth
  if (!editableFocused) return layoutHeight

  const candidates = [layoutHeight]
  const addMatchingWindowHeight = (width: number, height: number) => {
    if (
      Number.isFinite(width)
      && Number.isFinite(height)
      && height > 0
      && Math.round(width) === Math.round(layoutWidth)
    ) {
      candidates.push(height)
    }
  }

  addMatchingWindowHeight(browserWindow.outerWidth, browserWindow.outerHeight)
  addMatchingWindowHeight(browserWindow.screen.width, browserWindow.screen.height)
  return Math.max(...candidates)
}

export function installVisualViewportCssVariables(
  browserWindow: Window = window,
  root: HTMLElement = document.documentElement,
): () => void {
  const viewport = browserWindow.visualViewport
  const ownerDocument = root.ownerDocument
  const standalone = browserWindow.matchMedia?.('(display-mode: standalone)').matches ?? false
  const runtimeForCurrentEnvironment = () => classifyViewportRuntime({
    nativePlatform: getNativePlatform(browserWindow),
    previewDeviceOverride: readPreviewDeviceOverride(),
    primaryPointer: browserWindow.matchMedia?.('(pointer: coarse)').matches
      ? 'coarse'
      : browserWindow.matchMedia?.('(pointer: fine)').matches
        ? 'fine'
        : undefined,
    standalone,
    userAgent: browserWindow.navigator.userAgent,
  })
  let runtime = runtimeForCurrentEnvironment()
  let stableLayoutHeight = getInitialStableLayoutHeight(
    browserWindow,
    isEditableTarget(ownerDocument.activeElement),
  )
  let stableLayoutWidth = browserWindow.innerWidth
  let animationFrame: number | null = null
  let revealedFor: { element: Element | null; height: number } = { element: null, height: 0 }

  const writeRuntime = () => {
    root.dataset.viewportDisplayMode = runtime.displayMode
    root.dataset.viewportFormFactor = runtime.formFactor
    root.dataset.viewportGeometry = runtime.geometryMode
    root.dataset.viewportInputMode = runtime.inputMode
    root.dataset.viewportPlatform = runtime.platform
    root.dataset.viewportSource = readPreviewDeviceOverride() === 'off' ? 'runtime' : 'preview-override'
  }
  writeRuntime()

  const setPixelProperty = (property: (typeof VIEWPORT_CSS_PROPERTIES)[number], value: number) => {
    const next = `${value}px`
    if (root.style.getPropertyValue(property) !== next) root.style.setProperty(property, next)
  }

  const update = () => {
    animationFrame = null
    const nextRuntime = runtimeForCurrentEnvironment()
    if (
      nextRuntime.displayMode !== runtime.displayMode
      || nextRuntime.formFactor !== runtime.formFactor
      || nextRuntime.geometryMode !== runtime.geometryMode
      || nextRuntime.inputMode !== runtime.inputMode
      || nextRuntime.platform !== runtime.platform
    ) {
      runtime = nextRuntime
      writeRuntime()
    }
    const layoutHeight = browserWindow.innerHeight
    const layoutWidth = browserWindow.innerWidth
    const editableFocused = isEditableTarget(ownerDocument.activeElement)
    if (layoutWidth !== stableLayoutWidth) {
      stableLayoutHeight = getInitialStableLayoutHeight(browserWindow, editableFocused)
      stableLayoutWidth = layoutWidth
    } else if (!editableFocused) {
      stableLayoutHeight = Math.max(stableLayoutHeight, layoutHeight)
    }
    const metrics = runtime.geometryMode === 'layout-resize'
      ? calculateLayoutResizeMetrics({
          editableFocused,
          layoutHeight,
          layoutWidth,
          stableLayoutHeight,
          viewport,
        })
      : calculateVisualViewportMetrics({
          editableFocused,
          layoutHeight,
          layoutWidth,
          viewport,
        })

    setPixelProperty('--visual-viewport-bottom-inset', metrics.bottomInset)
    setPixelProperty('--visual-viewport-height', metrics.height)
    setPixelProperty('--visual-viewport-offset-left', metrics.offsetLeft)
    setPixelProperty('--visual-viewport-offset-top', metrics.offsetTop)
    setPixelProperty('--visual-viewport-width', metrics.width)
    const keyboardState = metrics.keyboardVisible ? 'open' : 'closed'
    if (root.dataset.visualViewportKeyboard !== keyboardState) {
      root.dataset.visualViewportKeyboard = keyboardState
    }
    // Only when focus moved or the usable height changed: a reader scrolling away from the caret is never pulled back.
    const focused = editableFocused ? ownerDocument.activeElement : null
    if (focused && (focused !== revealedFor.element || metrics.height !== revealedFor.height) && runtime.inputMode === 'touch') revealFocusedLine(browserWindow, ownerDocument)
    revealedFor = { element: focused, height: metrics.height }
  }

  const scheduleUpdate = () => {
    if (animationFrame !== null) return
    animationFrame = browserWindow.requestAnimationFrame(update)
  }

  update()
  browserWindow.addEventListener('resize', scheduleUpdate, { passive: true })
  browserWindow.addEventListener('orientationchange', scheduleUpdate, { passive: true })
  viewport?.addEventListener('resize', scheduleUpdate, { passive: true })
  if (runtime.geometryMode === 'visual-viewport') {
    viewport?.addEventListener('scroll', scheduleUpdate, { passive: true })
  }
  ownerDocument.addEventListener('focusin', scheduleUpdate)
  ownerDocument.addEventListener('focusout', scheduleUpdate)
  browserWindow.addEventListener(PREVIEW_DEVICE_OVERRIDE_CHANGE_EVENT, scheduleUpdate)

  return () => {
    browserWindow.removeEventListener('resize', scheduleUpdate)
    browserWindow.removeEventListener('orientationchange', scheduleUpdate)
    viewport?.removeEventListener('resize', scheduleUpdate)
    if (runtime.geometryMode === 'visual-viewport') {
      viewport?.removeEventListener('scroll', scheduleUpdate)
    }
    ownerDocument.removeEventListener('focusin', scheduleUpdate)
    ownerDocument.removeEventListener('focusout', scheduleUpdate)
    browserWindow.removeEventListener(PREVIEW_DEVICE_OVERRIDE_CHANGE_EVENT, scheduleUpdate)
    if (animationFrame !== null) browserWindow.cancelAnimationFrame(animationFrame)
    for (const property of VIEWPORT_CSS_PROPERTIES) root.style.removeProperty(property)
    delete root.dataset.visualViewportKeyboard
    delete root.dataset.viewportDisplayMode
    delete root.dataset.viewportFormFactor
    delete root.dataset.viewportGeometry
    delete root.dataset.viewportInputMode
    delete root.dataset.viewportPlatform
    delete root.dataset.viewportSource
  }
}
